import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, InputNumber, message, Select, Spin, Tooltip, Typography } from 'antd';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import ErrorResult from '../../components/ErrorResult';
import Form from '../../components/Form';
import { withRouteChange } from '../../services/APM';

interface VixtingAdmissionSettingsFormValues {
  vixtingDaysToAdmission: number;
  vixtingAdmissionDateReference: 'admission_date' | 'current_date';
}

const VixtingAdmissionSettings = () => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm<VixtingAdmissionSettingsFormValues>();
  const config = useAPIQuery('/configs/tenant-configurations/');

  const mutation = useAPIMutation(
    async (values: VixtingAdmissionSettingsFormValues, api) => {
      return await Promise.all([
        api.patch('/configs/tenant-configurations/vixting-days-to-admission/', {
          value: values.vixtingDaysToAdmission,
        }),
        api.patch('/configs/tenant-configurations/vixting-admission-date-reference/', {
          value: values.vixtingAdmissionDateReference,
        }),
      ]);
    },
    {
      onSuccess: () => {
        message.success('Configurações atualizadas.');
      },
      onSettled: async () => {
        await queryClient.invalidateQueries('/configs/tenant-configurations/');
      },
    }
  );

  return (
    <ErrorResult error={config.error}>
      <Helmet>
        <title>Vixting data limite de admissão</title>
      </Helmet>
      <Spin spinning={config.isLoading}>
        <Form<VixtingAdmissionSettingsFormValues>
          form={form}
          layout="vertical"
          initialValues={{
            vixtingDaysToAdmission: config.data?.vixtingDaysToAdmission,
            vixtingAdmissionDateReference: config.data?.vixtingAdmissionDateReference,
          }}
          onFinish={(values) => {
            mutation.mutate(values);
          }}
        >
          <Form.Legend>Data limite de admissão</Form.Legend>
          <Typography.Paragraph>
            Escolha o critério para calcular a data limite na etapa de contratação.
          </Typography.Paragraph>
          <Form.Col span={12}>
            <Form.Item
              name="vixtingDaysToAdmission"
              label="Quantidade de dias"
              rules={[{ type: 'number' }]}
            >
              <InputNumber min={1} max={30} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="vixtingAdmissionDateReference"
              label={
                <Typography.Text>
                  Referência da data limite de admissão{' '}
                  <Tooltip
                    overlayStyle={{ maxWidth: '400px' }}
                    title={
                      <div style={{ textAlign: 'center' }}>
                        <strong>Pela data de admissão:</strong> a data limite será calculada
                        subtraindo a data de admissão do candidato pela quantidade de dias definida.
                        <br />
                        <strong>Pela data atual:</strong> a data limite será calculada somando a
                        quantidade de dias definida à data de hoje.
                      </div>
                    }
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </Typography.Text>
              }
              rules={[{ type: 'string' }]}
            >
              <Select>
                <Select.Option value="admission_date">Data de admissão</Select.Option>
                <Select.Option value="current_date">Data atual</Select.Option>
              </Select>
            </Form.Item>
          </Form.Col>
          <Form.Footer>
            <Form.FooterEnd>
              <Button type="primary" loading={mutation.isLoading} htmlType="submit">
                Salvar alterações
              </Button>
            </Form.FooterEnd>
          </Form.Footer>
        </Form>
      </Spin>
    </ErrorResult>
  );
};

export default withRouteChange()(VixtingAdmissionSettings);
